import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getSearchCorrectedTerm, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getKeywordFromQueryParams, parseQueryParams } from '@/utils/queryParams';
import { urlOnlyRewriteRouterState } from '@/utils/urlOnlyRewriteRouterState';
import { buildQueryUrl, getIsSearchPage, getIsSEOSearchPage } from '@/utils/urls';

/**
 * When search-party answered a typed search with results for a corrected keyword, rewrite the URL to
 * `keyword=<corrected>&originalKeyword=<typed>`. Every link the page builds (paging, filters, sort, tabs,
 * Follow, a shared link) reads `keyword`, so they all stay on the corrected results, and `originalKeyword`
 * keeps the "Search instead for" message on them.
 */
export const useReplaceCorrectedSearchUrl = () => {
    const navigate = useNavigate();
    const { hash, key, pathname, search } = useLocation();
    const { isInitialLoading, submitted } = useAppSelector(getSearchUiState);
    const queryParams = useMemo(() => parseQueryParams(search), [search]);
    const keyword = String(getKeywordFromQueryParams(queryParams));
    const correctedSearchTerm = useAppSelector((state) => getSearchCorrectedTerm(state, keyword));
    const loadingSeenForLocationKeyRef = useRef<string | null>(null);

    useEffect(() => {
        const isKeywordSearchPage = getIsSearchPage(pathname) && !getIsSEOSearchPage(pathname);
        if (!isKeywordSearchPage || !keyword || queryParams.originalKeyword) {
            return;
        }
        if (isInitialLoading || submitted) {
            loadingSeenForLocationKeyRef.current = key;
            return;
        }
        // On a client-side arrival this effect runs before RouteDataLoader's (a parent) starts the search, so the
        // store still holds the previous page's correction. Trust it only once this location has loaded, or on the
        // server-rendered entry (`default`), whose correction came with the page.
        if (!correctedSearchTerm || (key !== 'default' && loadingSeenForLocationKeyRef.current !== key)) {
            return;
        }

        // The page already shows the corrected results, so don't search again or jump to the top.
        navigate(
            `${buildQueryUrl(pathname, { ...queryParams, keyword: correctedSearchTerm, originalKeyword: keyword })}${hash}`,
            { replace: true, state: urlOnlyRewriteRouterState }
        );
    }, [correctedSearchTerm, hash, isInitialLoading, key, keyword, navigate, pathname, queryParams, submitted]);
};
